import { stripUndefined } from '@customerio/hydra-validation';
import type { GeosetsFenceRequest } from '@customerio/hydra-ui-api';
import { isRecord } from '@/utils/objects';
import type { Geofence, GeofenceWriteInput } from '../geofence/entity';
import type { GeosetFence } from './entity';

/**
 * Pure bridging between the geoset fence wire model and the workspace
 * `Geofence` shape the geofences UI is built around. Kept free of hook/API
 * imports so the mapping is unit-testable without the API client.
 *
 * The geoset fence carries the same data as a `Geofence`; the only rename is
 * the free-form bag: `attributes` on the wire ↔ `metadata` in the UI (address,
 * saved radius unit, location input).
 *
 * The geoset `attributes` bag only stores scalars — the backend rejects nested
 * objects/arrays (422). The UI `metadata` models the address as a nested
 * `{ formatted_address, place_id, … }` object, so the adapter flattens that
 * object into `address_<subkey>` scalar keys on write and reconstructs the
 * nested shape on read, keeping the UI helpers (`formatGeofenceAddress`,
 * `getGeofenceAddressPlaceId`) working against `metadata.address` unchanged.
 */

const ADDRESS_METADATA_KEY = 'address';
const ADDRESS_ATTRIBUTE_PREFIX = 'address_';

/**
 * Reconstruct the nested `metadata.address` object from the flat
 * `address_<subkey>` scalar attributes written by {@link flattenAddressMetadata}.
 */
function nestAddressAttributes(attributes: Record<string, unknown>): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  const address: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (key.startsWith(ADDRESS_ATTRIBUTE_PREFIX)) {
      address[key.slice(ADDRESS_ATTRIBUTE_PREFIX.length)] = value;
    } else {
      metadata[key] = value;
    }
  }

  if (Object.keys(address).length > 0) {
    metadata[ADDRESS_METADATA_KEY] = address;
  }

  return metadata;
}

/** Coerce the wire `attributes` to the plain-object `metadata` the UI reads. */
function attributesToMetadata(attributes: unknown): Record<string, unknown> {
  return isRecord(attributes) ? nestAddressAttributes(attributes) : {};
}

/**
 * Flatten the nested `metadata.address` object into `address_<subkey>` scalar
 * keys so the geoset `attributes` bag stays free of nested objects. Other
 * metadata keys (already scalars) pass through untouched.
 */
function flattenAddressMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  const { [ADDRESS_METADATA_KEY]: address, ...rest } = metadata;

  if (!isRecord(address)) {
    return metadata;
  }

  const flattened: Record<string, unknown> = { ...rest };
  for (const [key, value] of Object.entries(address)) {
    flattened[`${ADDRESS_ATTRIBUTE_PREFIX}${key}`] = value;
  }

  return flattened;
}

/** Adapt a geoset fence read model into the workspace `Geofence` shape. */
export function geosetFenceToGeofence(fence: GeosetFence): Geofence {
  return {
    id: fence.id,
    name: fence.name,
    latitude: fence.latitude,
    longitude: fence.longitude,
    radius: fence.radius,
    external_id: fence.external_id,
    enabled: fence.enabled,
    metadata: attributesToMetadata(fence.attributes),
    geoset_ids: (fence.geoset_ids ?? []).map(String),
    created_at: fence.created_at,
    updated_at: fence.updated_at,
  };
}

type GeosetFenceRequestBody = GeosetsFenceRequest['fence'];

/**
 * Adapt a geofence write payload into the geoset fence request body, mapping
 * `metadata` → `attributes` and dropping undefined keys (the API treats an
 * absent key as "preserve", so partial updates must omit, not null out).
 */
export function geofenceWriteToFenceRequest(input: GeofenceWriteInput): GeosetFenceRequestBody {
  const { metadata, ...rest } = input;
  const attributes = metadata == null ? undefined : flattenAddressMetadata(metadata);
  return stripUndefined({ ...rest, attributes });
}
